// services/syncService.js
import appURL from '../../appURL.json';
import { transformInstitutionMapping, transformSyncAccounts, transformSyncCycle, transformSyncEvaluationTemplate, transformSyncHolidays, transformSyncItems, transformSyncPolicies, transformSyncPresence, transformSyncReasons, transformSyncSessionJointDays, transformSyncStockItems, transformSyncTerritories, transformSyncUnits, transformSyncWorksheets } from '../../components/OpalSyncFactoryData/OpalSyncFactoryData';
import { clearAllLocalData, syncDataToLocal } from '../../localDB';
import { transformSyncSession } from '../../components/OpalCoverageLayout/CoverageFactoryData/CoverageFactoryData';
import { Agent, Announcements, EvaluationTemplates, Client } from '../../visualizations/visualizations';


// 🔁 Generic paginated sync function
const fetchPaginatedSync = async ({ storageKey, url, resultKey, method = "POST", forced = false, group = false }) => {
    let page = Number(localStorage.getItem(`lastSyncedPage_${storageKey}`)) || 1;
    let hasNext = true;
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_${storageKey}`) || "[]");

    const userGroupIDs = localStorage.getItem('usr_g');

    try {
        while (hasNext) {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            let fullUrl = `${url}/${page}`;

            if (forced || group) {
                const params = new URLSearchParams();
                if (forced) params.append('Forced', 'True');
                if (group && userGroupIDs) params.append('Groups', userGroupIDs);
                fullUrl += `?${params.toString()}`;
            }

            const response = await fetch(fullUrl, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-Device-ID": devId
                }
            });

            if (!response.ok) {
                localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
                localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
                return { success: false, error: `Sync failed for ${storageKey} at page ${page}: ${response.statusText}` };
            }

            const result = await response.json();
            const pageData = result[resultKey];
            hasNext = result.has_next;

            accumulated = [...accumulated, ...pageData];
            page += 1;

            localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
            localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
        }

        localStorage.removeItem(`lastSyncedPage_${storageKey}`);
        localStorage.removeItem(`syncedData_${storageKey}`);

        return { success: true, data: accumulated };
    } catch (error) {
        localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
        localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchPoliciesPaginatedSync = async ({ storageKey, url, resultKey, forced = false }) => {
    const token = localStorage.getItem("token");
    const devId = localStorage.getItem("devId");
    const userGroupIDs = localStorage.getItem("usr_g");

    // Split multiple IDs (e.g. "2,3") -> [2, 3]
    const groupList = userGroupIDs
        ? userGroupIDs.split(',').map(id => id.trim()).filter(Boolean)
        : [];

    const allGroupData = [];

    try {
        // 🔁 Outer loop for each user group
        for (const groupID of groupList) {
            let page = Number(localStorage.getItem(`lastSyncedPage_${storageKey}_${groupID}`)) || 1;
            let hasNext = true;
            let accumulated = JSON.parse(localStorage.getItem(`syncedData_${storageKey}_${groupID}`) || "[]");

            // 🔁 Inner loop for pagination
            while (hasNext) {
                let fullUrl = `${url}/${page}/${groupID}`;
                if (forced) fullUrl += `?Forced=True`;

                const response = await fetch(fullUrl, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                        "X-ETR-Device-ID": devId
                    }
                });

                if (!response.ok) {
                    // Save tracing before returning
                    localStorage.setItem(`lastSyncedPage_${storageKey}_${groupID}`, page);
                    localStorage.setItem(`syncedData_${storageKey}_${groupID}`, JSON.stringify(accumulated));
                    return { success: false, error: `Sync failed for ${storageKey} (Group ${groupID}) at page ${page}: ${response.statusText}` };
                }

                const result = await response.json();
                const pageData = result[resultKey] || [];
                hasNext = result.has_next;

                accumulated = [...accumulated, ...pageData];
                page += 1;

                // ✅ Save tracing progress after each page
                localStorage.setItem(`lastSyncedPage_${storageKey}_${groupID}`, page);
                localStorage.setItem(`syncedData_${storageKey}_${groupID}`, JSON.stringify(accumulated));
            }

            // ✅ After pagination completes, wrap data
            allGroupData.push({
                id: Number(groupID),
                [resultKey]: accumulated
            });

            // Clear tracing for this group
            localStorage.removeItem(`lastSyncedPage_${storageKey}_${groupID}`);
            localStorage.removeItem(`syncedData_${storageKey}_${groupID}`);
        }

        return { success: true, data: allGroupData };
    } catch (error) {
        // Fallback tracing if unexpected error
        localStorage.setItem(`lastSyncError_${storageKey}`, error.message || "Unexpected sync error");
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};


const fetchCyclePaginatedSync = async (forced = false) => {
    let page = Number(localStorage.getItem(`lastSyncedPage_cycles`)) || 1;
    let hasNext = true;
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_cycles`) || "[]");

    const userGroupIDs = localStorage.getItem('usr_g');

    try {
        while (hasNext) {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            const fullUrl = `${appURL.API_Sync}/administration/cycles/${userGroupIDs}/${page}${forced ? '?Forced=true' : ''}`;

            const response = await fetch(fullUrl, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-Device-ID": devId
                }
            });

            if (!response.ok) {
                localStorage.setItem(`lastSyncedPage_cycles`, page);
                localStorage.setItem(`syncedData_cycles`, JSON.stringify(accumulated));
                return { success: false, error: `Sync failed for cycle at page ${page}: ${response.statusText}` };
            }

            const result = await response.json();
            const pageData = result["cycles"];
            hasNext = result.has_next;

            accumulated = [...accumulated, ...pageData];
            page += 1;

            localStorage.setItem(`lastSyncedPage_cycles`, page);
            localStorage.setItem(`syncedData_cycles`, JSON.stringify(accumulated));
        }

        localStorage.removeItem(`lastSyncedPage_cycles`);
        localStorage.removeItem(`syncedData_cycles`);

        return { success: true, data: accumulated };
    } catch (error) {
        localStorage.setItem(`lastSyncedPage_cycles`, page);
        localStorage.setItem(`syncedData_cycles`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchPaginatedAccountSync = async ({ storageKey, url, resultKey, method = "POST", forced = false }) => {
    let page = Number(localStorage.getItem(`lastSyncedPage_${storageKey}`)) || 1;
    let hasNext = true;
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_${storageKey}`) || "[]");

    const userGroupIDs = localStorage.getItem('usr_g');

    try {
        while (hasNext) {
            const token = localStorage.getItem("token");
            const devId = localStorage.getItem("devId");
            let fullUrl = `${url}/${page}/${userGroupIDs}`;

            if (forced) {
                const params = new URLSearchParams();
                if (forced) params.append('Forced', 'True');
                fullUrl += `?${params.toString()}`;
            }

            const response = await fetch(fullUrl, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                    "X-ETR-Device-ID": devId
                }
            });

            if (!response.ok) {
                localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
                localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
                return { success: false, error: `Sync failed for ${storageKey} at page ${page}: ${response.statusText}` };
            }

            const result = await response.json();
            const pageData = result[resultKey];
            hasNext = result.has_next;

            accumulated = [...accumulated, ...pageData];
            page += 1;

            localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
            localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
        }

        localStorage.removeItem(`lastSyncedPage_${storageKey}`);
        localStorage.removeItem(`syncedData_${storageKey}`);

        return { success: true, data: accumulated };
    } catch (error) {
        localStorage.setItem(`lastSyncedPage_${storageKey}`, page);
        localStorage.setItem(`syncedData_${storageKey}`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchSessionSync = async (sessionDates) => {
    let currentDate = null;

    let accumulatedSessions = JSON.parse(localStorage.getItem(`syncedData_sessions`) || "[]");
    let accumulatedEvents = JSON.parse(localStorage.getItem(`syncedData_events`) || "[]");
    let accumulatedJoint = JSON.parse(localStorage.getItem(`syncedData_joint`) || "[]");

    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');
        const userGroupIDs = localStorage.getItem('usr_g');

        for (const sessionDate of sessionDates) {

            currentDate = sessionDate;

            const response = await fetch(`${appURL.API_Session}?d=${sessionDate}&g=${userGroupIDs}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-ETR-DeviceID': devId
                },
            });

            if (!response.ok) {
                localStorage.setItem(`lastSyncedPage_session`, sessionDate);
                localStorage.setItem(`syncedData_sessions`, JSON.stringify(accumulatedSessions));
                localStorage.setItem(`syncedData_events`, JSON.stringify(accumulatedEvents));
                localStorage.setItem(`syncedData_joint`, JSON.stringify(accumulatedJoint));

                return { success: false, error: `Sync failed for date ${sessionDate}: ${response.statusText}` };
            }

            const result = await response.json();
            const cycleID = Number(result?.cycle?.id);

            // SESSIONS
            const transformedSessions = (result.sessions || []).map(item => ({
                ...item,
                date: sessionDate,
                cycleID
            }));

            accumulatedSessions = [
                ...accumulatedSessions,
                ...transformedSessions
            ];

            // EVENTS
            const transformedEvents = (result.events || []).map(item => ({
                ...item,
                date: sessionDate,
                cycleID
            }));

            accumulatedEvents = [
                ...accumulatedEvents,
                ...transformedEvents
            ];

            const jointValue = result.joint || false;

            accumulatedJoint.push({
                date: sessionDate,
                joint: jointValue
            });

            // Save separately
            localStorage.setItem(`lastSyncedPage_session`, sessionDate);
            localStorage.setItem(`syncedData_sessions`, JSON.stringify(accumulatedSessions));
            localStorage.setItem(`syncedData_events`, JSON.stringify(accumulatedEvents));
            localStorage.setItem(`syncedData_joint`, JSON.stringify(accumulatedJoint));
        }

        // Clear temp markers
        localStorage.removeItem(`lastSyncedPage_session`);

        return {
            success: true,
            sessions: accumulatedSessions,
            events: accumulatedEvents,
            joint: accumulatedJoint
        };

    } catch (error) {

        localStorage.setItem(`lastSyncedPage_session`, currentDate);
        localStorage.setItem(`syncedData_sessions`, JSON.stringify(accumulatedSessions));
        localStorage.setItem(`syncedData_events`, JSON.stringify(accumulatedEvents));
        localStorage.setItem(`syncedData_joint`, JSON.stringify(accumulatedJoint));

        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchEvaluationTemplateSync = async () => {
    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');
        const userGroupID = localStorage.getItem('usr_g');

        const response = await fetch(`${appURL.API_GetEvaluationTemplate}/${userGroupID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-ETR-Device-ID': devId
            },
        });

        if (!response.ok) {
            return {
                success: false,
                error: `Sync failed for evaluation template: ${response.statusText}`
            };
        }

        const result = await response.json();

        const templates = result.evaluation || [];

        return {
            success: true,
            data: templates
        };

    } catch (error) {
        return {
            success: false,
            error: error.message || 'Unexpected sync error'
        };
    }
};


const fetchPresenceSync = async (forced = false) => {
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_presence`) || "{}");

    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');

        const fullUrl = `${appURL.API_Sync}/operations/presence${forced ? '?Forced=true' : ''}`;

        const response = await fetch(fullUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-ETR-Device-ID': devId
            },
        });

        if (!response.ok) {
            // Save what we have and exit
            localStorage.setItem(`syncedData_presence`, JSON.stringify(accumulated));
            return { success: false, error: `Sync failed for presence` };
        }

        const result = await response.json();

        localStorage.removeItem(`syncedData_presence`);

        return { success: true, data: result };

    } catch (error) {
        localStorage.setItem(`syncedData_presence`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchUserWorksheetSync = async (id) => {
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_userWorksheet`) || "{}");

    const storedPermission = localStorage.getItem("permission");
    const permission = JSON.parse(atob(storedPermission));

    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');

        const fullUrl = `${appURL.API_Worksheet}/${Number(permission?.coverage?.employee_id)}/worksheets/${id}`;

        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-ETR-Device-ID': devId
            },
        });

        if (!response.ok) {
            localStorage.setItem(`syncedData_userWorksheet`, JSON.stringify(accumulated));
            return { success: false, error: `Sync failed for user worksheet` };
        }

        const result = await response.json();

        localStorage.removeItem(`syncedData_userWorksheet`);

        return { success: true, data: result?.plan?.days };

    } catch (error) {
        localStorage.setItem(`syncedData_userWorksheet`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchWorksheetSync = async (forced = false) => {
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_worksheet`) || "{}");

    const storedPermission = localStorage.getItem("permission");
    const permission = JSON.parse(atob(storedPermission));

    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');

        const fullUrl = `${appURL.API_Worksheet}/${Number(permission?.coverage?.employee_id)}/worksheets`;

        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-ETR-Device-ID': devId
            },
        });

        if (!response.ok) {
            localStorage.setItem(`syncedData_worksheet`, JSON.stringify(accumulated));
            return { success: false, error: `Sync failed for worksheet` };
        }

        const result = await response.json();
        let worksheets = result.worksheets || [];

        // fetch details for each worksheet in parallel
        const enriched = await Promise.all(
            worksheets.map(async (worksheet) => {
                const details = await fetchUserWorksheetSync(worksheet.id);
                if (details.success) {
                    return { ...worksheet, days: details.data };
                }
                return worksheet; // keep original if failed
            })
        );

        localStorage.removeItem(`syncedData_worksheet`);

        return { success: true, data: enriched };

    } catch (error) {
        localStorage.setItem(`syncedData_worksheet`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

const fetchInventorySampleStocksSync = async (forced = false) => {
    let accumulated = JSON.parse(localStorage.getItem(`syncedData_stocks`) || "{}");

    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');

        const userGroupIDs = localStorage.getItem('usr_g');
        
        const fullUrl = `${appURL.API_InventorySampleStocks}?Groups=${userGroupIDs}`;

        const response = await fetch(fullUrl, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-ETR-Device-ID': devId
            },
        });

        if (!response.ok) {
            localStorage.setItem(`syncedData_stocks`, JSON.stringify(accumulated));
            return { success: false, error: `Sync failed for item sample stocks` };
        }

        const result = await response.json();
        let stocks = result.stocks || [];

        localStorage.removeItem(`syncedData_stocks`);

        return { success: true, data: stocks };

    } catch (error) {
        localStorage.setItem(`syncedData_stocks`, JSON.stringify(accumulated));
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};


const fetchSyncDone = async () => {
    try {
        const token = localStorage.getItem('token');
        const devId = localStorage.getItem('devId');


        const response = await fetch(`${appURL.API_Sync}/done`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                'X-ETR-Device-ID': devId
            },
        });

        if (!response.ok) {
            return { success: false };
        }

        return { success: true };

    } catch (error) {
        return { success: false, error: error.message || "Unexpected sync error" };
    }
};

// 🔗 Main sync method — only this is exported
export const SyncData = async (forced = false) => {
    // if (forced) await clearAllLocalData();

    try {

        const cycleID = localStorage.getItem("c_id");
        const p_days = Number(localStorage.getItem("p_days")) || 0;
        const svr_date = localStorage.getItem("srv_date");
        const results = {};

        // Accounts by type
        const accountTypes = ["customer", "doctor", "agent", "institution"];
        const allAccounts = [];
        const allMissedCallReasons = [];

        const presence = await fetchPresenceSync(forced);
        if (presence.success) {
            results["presence"] = presence
        }

        const cycles = await fetchCyclePaginatedSync(forced);
        if (cycles.success) {
            results["cycles"] = cycles
        }

        const worksheets = await fetchWorksheetSync(forced);
        if (worksheets.success) {
            results["worksheets"] = worksheets
        }

        const itemSampleStocks = await fetchInventorySampleStocksSync(forced);
        if (itemSampleStocks.success) {
            results["stocks"] = itemSampleStocks
        }

        const sessionDates = [];
        if (svr_date && !isNaN(p_days)) {
            const baseDate = new Date(svr_date);
            for (let i = -p_days; i <= p_days; i++) {
                const newDate = new Date(baseDate);
                newDate.setDate(baseDate.getDate() + i);
                sessionDates.push(newDate.toLocaleDateString('en-CA'));
            }
        }

        const coverage = await fetchSessionSync(sessionDates);
        if (coverage.success) {
            results["sessions"] = coverage.sessions;
            results["events"] = coverage.events;
            results["joint"] = coverage.joint;
        }

        const templates = await fetchEvaluationTemplateSync();
        if (templates.success) {
            results["evaltemplate"] = templates;
        }

        const policies = await fetchPoliciesPaginatedSync({
            storageKey: "policies",
            url: `${appURL.API_Sync}/operations/policies`,
            resultKey: "policies",
            forced: forced
        });

        if (policies.success) {
            results["policies"] = policies;
        }

        for (const type of accountTypes) {
            const result = await fetchPaginatedAccountSync({
                storageKey: type,
                url: `${appURL.API_Sync}/accounts/${type}`,
                resultKey: "accounts",
                method: "POST",
                forced: forced
            });

            if (!result.success) {
                // return { success: false, message: `Failed to fetch ${type} accounts.` };
                continue
            }

            results[type] = result;
            const accountsWithType = result.data.map(account => ({ ...account, type }));
            allAccounts.push(...accountsWithType);
        }

        for (const type of accountTypes) {
            const combinedKey = `${type}_reasons`;

            const result = await fetchPaginatedSync({
                storageKey: combinedKey,
                url: `${appURL.API_Sync}/coverage/reasons/missed/${type}`,
                resultKey: "reasons",
                method: "POST",
                forced: forced
            });

            if (!result.success) {
                // return { success: false, message: `Failed to fetch ${type} reasons.` };
                continue
            }

            const accountsWithType = result.data.map(account => ({ ...account, type }));
            allMissedCallReasons.push(...accountsWithType);
        }

        // Other fetches
        const otherSyncs = [
            { key: "channels", url: `${appURL.API_Sync}/accounts/channels`, resultKey: "channels" },
            { key: "classifications", url: `${appURL.API_Sync}/accounts/classifications`, resultKey: "classifications" },
            { key: "frequencies", url: `${appURL.API_Sync}/coverage/frequency`, resultKey: "frequencies" },
            { key: "best_days", url: `${appURL.API_Sync}/coverage/best-day`, resultKey: "best_days" },
            { key: "best_times", url: `${appURL.API_Sync}/coverage/best-time`, resultKey: "best_times" },
            { key: "items", url: `${appURL.API_Sync}/inventory/items`, resultKey: "items", group: true },
            { key: "units", url: `${appURL.API_Sync}/inventory/items/units`, resultKey: "units" },
            { key: "holidays", url: `${appURL.API_Sync}/holidays`, resultKey: "holidays" },
            { key: "reasons", url: `${appURL.API_Sync}/coverage/reasons/advanced`, resultKey: "reasons" },
            { key: "territories", url: `${appURL.API_Sync}/administration/mapping/territory/${cycleID}`, resultKey: "territories" }
        ];

        for (const sync of otherSyncs) {
            const result = await fetchPaginatedSync({
                storageKey: sync.key,
                url: sync.url,
                resultKey: sync.resultKey,
                forced: forced,
                group: sync.group
            });

            if (!result.success) {
                // return { success: false, message: `Failed to fetch ${sync.key}.` };
                results[sync.key] = [];
                continue
            }

            results[sync.key] = result;
        }

        const combined = [
            ...(results["sessions"] || []),
            ...(results["events"] || [])
        ];

        const institution = allAccounts.filter(a => a.type === "institution");
        const transformPresence = await transformSyncPresence(results["presence"]?.data);
        const transformSessions = await transformSyncSession(combined);
        const transformInstitution = await transformInstitutionMapping(institution);
        const transformTerritories = await transformSyncTerritories(results["territories"]?.data);
        const transformAccounts = await transformSyncAccounts(allAccounts, results);
        const transformItems = await transformSyncItems(results["items"]?.data || []);
        const transformUnits = await transformSyncUnits(results["units"]?.data);
        const transformReasons = await transformSyncReasons(results["reasons"]?.data);
        const transformMissedCallReasons = await transformSyncReasons(allMissedCallReasons);
        const transformHolidays = await transformSyncHolidays(results["holidays"]?.data);
        const transformCycle = await transformSyncCycle(results["cycles"]?.data);
        const transformWorksheets = await transformSyncWorksheets(results["worksheets"]?.data);
        const transformPolicies = await transformSyncPolicies(results["policies"]?.data);
        const transformEvaluationTemplate = await transformSyncEvaluationTemplate(results["evaltemplate"]?.data);
        const transformJoint = await transformSyncSessionJointDays(results["joint"]);
        const transformItemStock = await transformSyncStockItems(results["stocks"]?.data || []);

        const syncResults = [
            await syncDataToLocal('Attendance', transformPresence),
            await syncDataToLocal('session', transformSessions),
            await syncDataToLocal('institution', transformInstitution),
            await syncDataToLocal('territories', transformTerritories),
            await syncDataToLocal('customer', transformAccounts),
            await syncDataToLocal('item', transformItems),
            await syncDataToLocal('unit', transformUnits),
            await syncDataToLocal('AdvancedCallReasons', transformReasons),
            await syncDataToLocal('MissedCallReasons', transformMissedCallReasons),
            await syncDataToLocal('holiday', transformHolidays),
            await syncDataToLocal('EvaluationTemplates', transformEvaluationTemplate),
            await syncDataToLocal('Announcements', Announcements),
            await syncDataToLocal('Cycle', transformCycle),
            await syncDataToLocal('Client', []),
            await syncDataToLocal('Worksheets', transformWorksheets),
            await syncDataToLocal('Policies', transformPolicies),
            await syncDataToLocal('SessionJointDays', transformJoint),
            await syncDataToLocal('StockAllocations', transformItemStock)
        ];
        // Find the first failed sync
        const failedSync = syncResults.find(res => !res.success);

        if (failedSync) {
            return { success: false, message: failedSync.message || 'Failed to sync some data to local DB.' };
        }

        return { success: true };
    } catch (error) {
        return { success: false, message: error.message || 'An unknown error occurred during sync.' };
    }
};

export const SyncDone = async () => {
    await fetchSyncDone();
}